/**
 * The Islamic calendar as a month and a list of what is coming.  Owner, 2026-10-20 (the calendar screen).
 *
 * Built on hijri.js: the tabular calendar plus the mosque's moon-sighting offset. Nothing here decides a date — the
 * server's Hijri date on each timetable day is the mosque's; this module (a) lays a Gregorian or a Hijri month out as
 * a grid, (b) names the days that matter, and (c) finds the offset the server is using so the phone can continue the
 * calendar past the days it has a timetable for, with the same shift.
 *
 * The important days are the ones a Norwegian mosque's calendar lists (CJAS, Ahl-e Sunnat): the new year, Ashura,
 * Mawlid, Isra and Mi'raj, Bara'at, the first of Ramadan, the 27th night, both Eids, Arafah — and the first day of
 * every month. Each is a KEY; the app's dictionary holds the words in three languages. What is computed is the tabular
 * date (shifted by the mosque's offset): the mosque announces the real night by sighting, and this says so in the app.
 */
const H = require('./hijri');

/** key → { month, day }. `night: true` = observed the evening before the date (the app says «natt til»). */
const IMPORTANT = [
  { key: 'newYear',      month: 1,  day: 1 },
  { key: 'ashura',       month: 1,  day: 10 },
  { key: 'mawlid',       month: 3,  day: 12 },
  { key: 'miraj',        month: 7,  day: 27, night: true },
  { key: 'baraat',       month: 8,  day: 15, night: true },
  { key: 'ramadanStart', month: 9,  day: 1 },
  { key: 'qadr',         month: 9,  day: 27, night: true },
  { key: 'eidFitr',      month: 10, day: 1 },
  { key: 'arafah',       month: 12, day: 9 },
  { key: 'eidAdha',      month: 12, day: 10 },
];

const DAY_MS = 86400000;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const local = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); };
const plusDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** What a day is: its important-day keys (an Eid and a month start can share a day; the month start is listed last). */
function marksOf(h) {
  const out = IMPORTANT.filter((x) => x.month === h.month && x.day === h.day).map((x) => ({ key: x.key, night: Boolean(x.night) }));
  if (h.day === 1) out.push({ key: 'monthStart', month: h.month });
  return out;
}

/**
 * The offset the server used for `serverHijri` on `date`: the one of -2..2 that makes the tabular calendar agree.
 * Null when none does (then the phone shows no Hijri number for days it has no timetable for, rather than a wrong one).
 */
function inferOffset(date, serverHijri) {
  if (!serverHijri || !serverHijri.year) return null;
  const d = typeof date === 'string' ? local(date) : date;
  for (const k of [0, 1, -1, 2, -2]) {
    const h = H.toHijri(d, k);
    if (h.year === serverHijri.year && h.month === serverHijri.month && h.day === serverHijri.day) return k;
  }
  return null;
}

/** One day for the grid: Gregorian and Hijri numbers, marks, and the timetable row when the phone has it. */
function cell(d, offset, rows) {
  const date = iso(d);
  const row = rows.get(date) || null;
  const h = row ? row.hijri : offset == null ? null : H.toHijri(d, offset);
  return { date, day: d.getDate(), weekday: (d.getDay() + 6) % 7, hijri: h, marks: h ? marksOf(h) : [], row };
}

/**
 * A Gregorian month as weeks, Monday first (core rule 7). `rows` = timetable days by date (their Hijri date is the
 * server's); `offset` = inferOffset() for the rest. Leading/trailing cells outside the month are null.
 */
function gregorianMonth(year, month, { rows = new Map(), offset = 0 } = {}) {
  const first = new Date(year, month - 1, 1);
  const n = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < (first.getDay() + 6) % 7; i++) cells.push(null);
  for (let d = 1; d <= n; d++) cells.push(cell(new Date(year, month - 1, d), offset, rows));
  while (cells.length % 7) cells.push(null);
  const weeks = []; for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const days = cells.filter(Boolean);
  return { year, month, weeks, hijriSpan: span(days) };
}

/**
 * A Hijri month as weeks, Monday first, found by walking the Gregorian days around `near` (a date inside or close to
 * it; the tabular calendar has no reverse conversion and needs none: 70 days cover any month and its neighbours).
 */
function hijriMonth(hYear, hMonth, { rows = new Map(), offset = 0, near = new Date() } = {}) {
  const start = plusDays(typeof near === 'string' ? local(near) : near, -70);
  const days = [];
  for (let i = 0; i < 140; i++) {
    const c = cell(plusDays(start, i), offset, rows);
    if (c.hijri && c.hijri.year === hYear && c.hijri.month === hMonth) days.push(c);
  }
  if (!days.length) return null;
  const cells = [];
  for (let i = 0; i < days[0].weekday; i++) cells.push(null);
  cells.push(...days);
  while (cells.length % 7) cells.push(null);
  const weeks = []; for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return { year: hYear, month: hMonth, weeks, gregorianSpan: { from: days[0].date, to: days[days.length - 1].date } };
}

/** The Hijri months a run of days touches: {from:{year,month}, to:{year,month}}. */
function span(days) {
  const hs = days.map((c) => c.hijri).filter(Boolean);
  if (!hs.length) return null;
  return { from: { year: hs[0].year, month: hs[0].month }, to: { year: hs[hs.length - 1].year, month: hs[hs.length - 1].month } };
}

/** Where the Hijri month after/before (y, m) is. */
function stepHijri(y, m, by) {
  let mm = m + by, yy = y;
  while (mm > 12) { mm -= 12; yy += 1; }
  while (mm < 1) { mm += 12; yy -= 1; }
  return { year: yy, month: mm };
}

/**
 * The important days from `from` (inclusive) for `days` days, nearest first: [{ date, inDays, hijri, key, night, month? }].
 * Month starts are included (they are what a calendar lists: «Rajab starter»).
 */
function upcoming(from, days = 120, { rows = new Map(), offset = 0 } = {}) {
  const start = typeof from === 'string' ? local(from) : new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const out = [];
  for (let i = 0; i < days; i++) {
    const c = cell(plusDays(start, i), offset, rows);
    for (const m of c.marks) out.push({ date: c.date, inDays: i, hijri: c.hijri, ...m });
  }
  return out;
}

module.exports = { IMPORTANT, marksOf, inferOffset, gregorianMonth, hijriMonth, stepHijri, upcoming, MONTHS_NO: H.MONTHS_NO, MONTHS_UR: H.MONTHS_UR };
